import { useRef, useState, useCallback } from 'react';

const useFileUploadInputProps = _ref => {
  let {
    inputProps,
    disabled,
    dropzoneRef,
    onFilesSelected
  } = _ref;
  // Destructure the input props that need to be merged with other event handlers
  const {
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    onClick: onClickProp,
    onKeyDown: onKeyDownProp,
    ...restInputProps
  } = inputProps || {};

  // Handle focus state and stuff related to input
  const inputRef = useRef(null);
  const divClickedRef = useRef(false);
  const [showFocus, setShowFocus] = useState(false);
  const handleDropzoneClick = useCallback(() => {
    if (!disabled) {
      divClickedRef.current = true;
      inputRef.current?.focus();
      inputRef.current?.click();
    }
  }, [disabled]);
  const onFocus = useCallback(e => {
    if (!divClickedRef.current) {
      setShowFocus(true);
    }
    // If the dropzone is the related target, it means the user clicked the
    // dropzone and the native file select UI will be opened. Once that closes,
    // the input is going to be refocused. We don't want to show the focus
    // state in that case, so wait to set divClickedRef to false until after
    // that second focus event.
    if (dropzoneRef.current && dropzoneRef.current !== e.relatedTarget) {
      divClickedRef.current = false;
    }
    if (onFocusProp) {
      onFocusProp(e);
    }
  }, [onFocusProp, dropzoneRef]);
  const onBlur = useCallback(e => {
    setShowFocus(false);
    if (onBlurProp) {
      onBlurProp(e);
    }
  }, [onBlurProp]);
  const onClick = useCallback(e => {
    // This keeps the StyledDropzone onClick from being triggered
    e.stopPropagation();
    if (!disabled && onClickProp) {
      onClickProp(e);
    }
  }, [onClickProp, disabled]);
  const onChange = useCallback(e => {
    if (!disabled) {
      const files = e.target.files;
      if (files && files.length) {
        onFilesSelected(files);
      }
      if (inputRef.current) {
        // clears <input> value after selection
        inputRef.current.value = "";
      }
    }
  }, [onFilesSelected, disabled]);
  const onKeyDown = useCallback(e => {
    if (!showFocus) {
      setShowFocus(true);
    }
    if (onKeyDownProp) {
      onKeyDownProp(e);
    }
  }, [onKeyDownProp, showFocus]);
  const mergedInputProps = {
    ...restInputProps,
    onFocus,
    onBlur,
    onClick,
    onChange,
    onKeyDown,
    ref: inputRef
  };
  const dropzoneProps = {
    onClick: handleDropzoneClick,
    showFocus
  };
  return {
    mergedInputProps,
    dropzoneProps
  };
};

export { useFileUploadInputProps };
